import { Controller } from "stimulus"
import { reportClientError } from "../utils/report_client_error"
import {
  PaymentChallengeError,
  takePaymentChallengeToken,
  waitForPaymentChallenge
} from "../utils/payment_security"
import { stripeBillingDetails } from "../utils/stripe_billing_details"

// Both Donation Flow variants use one embedded Stripe Payment Element. For monthly gifts, this
// PaymentIntent is also the first contribution and saves the method for later off-session charges.
//
// A future-start pledge (Flows::PledgeStart) changes what's charged today: its first meantime
// donation, or -- with no meantime donation -- nothing, in which case the endpoint returns a
// SetupIntent instead (intent_type "setup") and this only saves the payment details. UK bank
// accounts can't be saved that way in the Payment Element, so those donors get a separate button
// to Stripe's hosted page (bacsSetup target, StripeController#create_donation_flow_bacs_setup_session).
export default class extends Controller {
  static targets = ["expressCheckoutElement", "paymentElement", "paymentErrors", "paymentIntentId", "setupIntentId", "checkoutAttemptId", "paymentProcessorMethodId", "paymentProcessorMethodType", "paymentMethodId", "bacsSetup"]

  static values = {
    stripeApiKey: String,
    paymentIntentUrl: String,
    bacsSetupUrl: String,
    clientErrorUrl: String,
    variant: String,
    countryIso: Object
  }

  connect() {
    this.element.addEventListener("donation-wizard:pageShown", this.handlePageShown.bind(this))
    // The tip selector lives on the Review step alongside this Payment Element -- changing it must
    // re-issue the PaymentIntent, or the amount won't match at settlement. tipChanged: false still
    // notifies OFTW's review summary (Charged today) without starting another Stripe setup.
    this._onTipChanged = (event) => {
      if (!this._reviewActive) return
      if (event.detail?.tipChanged === false) return
      // The 0ms setup queued on arriving at Review hasn't run yet and will read this tip --
      // re-queueing would only push it back 400ms.
      if (this._reviewSetupPending) return
      this.queueSetupPaymentElement(400)
    }
    this.element.addEventListener("hli-tip:changed", this._onTipChanged)
    this.stripe = null
    this.elements = null
    this.isSubmitting = false
    this.intentKey = null
    this.intentType = null
    this._reviewActive = false
    this._setupGeneration = 0
  }

  disconnect() {
    this.element.removeEventListener("hli-tip:changed", this._onTipChanged)
    clearTimeout(this._setupDebounce)
  }

  handlePageShown(event) {
    if (event.detail.pageId !== "review") {
      this._reviewActive = false
      // Cancel a tip-edit debounce and any in-flight setup that would otherwise create a Stripe
      // Customer+Intent (and burn a Turnstile token) after the donor has left Review.
      clearTimeout(this._setupDebounce)
      this._reviewSetupPending = false
      this._setupGeneration += 1
      this._challengeAbort?.abort()
      return
    }
    this._reviewActive = true
    // Client controllers refresh hli-tip on this same event and rewrite tip_cents. Share one
    // debounced setup queue so tip:changed and this handler don't start two Stripe
    // Customer+Intent chains; delay 0 lets those sync tip refreshes finish first, and
    // _reviewSetupPending stops their tip:changed from pushing it back to 400ms.
    this.queueSetupPaymentElement(0)
    this._reviewSetupPending = true
  }

  // Prefer this over calling setupPaymentElement() directly so tip edits and variant toggles share
  // one debounced queue (and cancel each other) instead of overlapping Customer+Intent creates.
  queueSetupPaymentElement(delayMs) {
    clearTimeout(this._setupDebounce)
    this._reviewSetupPending = false
    this._setupDebounce = setTimeout(() => {
      this._reviewSetupPending = false
      this.setupPaymentElement()
    }, delayMs)
  }

  currentAmountCents() {
    const donation = parseInt(this.element.querySelector('[name="donation_form[amount_cents]"]')?.value, 10) || 0
    const tip = parseInt(this.element.querySelector('[name="donation_form[tip_cents]"]')?.value, 10) || 0
    return donation + tip
  }

  // Everything the server needs to decide what's charged today -- an intent is re-issued whenever
  // any of it changes, not just the amount (a different start month or meantime donation changes
  // today's charge, or whether there's one at all). Also where the gift goes: the checkout attempt
  // snapshots the form when its intent is issued, and DonationCheckoutAttempts::Finalize saves the
  // pledge's portfolio from that snapshot, so a portfolio or custom split changed after Review
  // must re-issue it too.
  currentIntentKey() {
    const field = (name) => this.element.querySelector(`[name="donation_form[${name}]"]`)?.value || ""
    return [this.currentAmountCents(), field("tip_cents"), field("start_at_month"), field("start_at_year"),
      field("trial_amount_cents"), this.variantValue, field("managed_portfolio_id"), this.customAllocationsKey()].join("|")
  }

  // Only what's actually submitted -- FormData leaves out a disabled split (one-for-the-world-donation
  // #syncCustomMix disables it when the mix is saved as an existing portfolio instead).
  customAllocationsKey() {
    return [...new FormData(this.element).entries()]
      .filter(([name]) => name.startsWith("donation_form[custom_allocations]"))
      .map(([name, value]) => `${name}=${value}`)
      .join("&")
  }

  async setupPaymentElement() {
    this.clearError()
    const clientId = this.element.querySelector('[name="donation_form[client_id]"]')?.value
    const currencyCode = this.element.querySelector('[name="donation_form[currency_code]"]')?.value
    // The PaymentIntent charges the full amount, tip included -- Contributions::CreateContributionFromPaymentIntent
    // expects intent.amount to equal amount_cents + tips_cents.
    const amountCents = this.currentAmountCents()

    if (!clientId || !currencyCode || !amountCents) {
      // Supersede any in-flight setup so it can't mount (or clear this error) for the old amount.
      this._setupGeneration += 1
      this._challengeAbort?.abort()
      this.showError("Please complete the amount step before continuing.")
      return
    }

    // Re-showing the review step without having changed the amount, start date or variant. The
    // variant check matters even when the amount is unchanged -- toggling one_time/recurrent on
    // Review must not keep confirming a PaymentIntent tagged with the variant the donor switched away from.
    const intentKey = this.currentIntentKey()
    if (this.elements && intentKey === this.intentKey) return

    // Something changed: drop any stale element so a failed re-fetch below can't leave confirmAndSubmit() confirming a Payment Element tied to the previous intent.
    this.elements = null
    this.intentKey = null
    this.intentType = null
    if (this.hasBacsSetupTarget) this.bacsSetupTarget.classList.add("is-hidden")
    this.showPaymentLoading()

    // Tip edits / variant toggles can overlap; only the latest setup may mount or show errors.
    const setupGeneration = ++this._setupGeneration
    this._challengeAbort?.abort()
    this._challengeAbort = new AbortController()

    try {
      await waitForPaymentChallenge(this.element, { signal: this._challengeAbort.signal })
      if (setupGeneration !== this._setupGeneration) return

      const body = new FormData(this.element)
      // set(), not append() -- the form already carries a `variant` field (kept in sync by
      // selectVariant()), and set() replaces it instead of sending a second, order-dependent value.
      body.set("variant", this.variantValue)
      body.set("donation_form[amount_cents]", amountCents - (parseInt(body.get("donation_form[tip_cents]"), 10) || 0))
      // Consume the token before fetch so an overlapping setup cannot post the same single-use
      // Turnstile token (siteverify would reject the second use).
      body.append("payment_challenge_token", takePaymentChallengeToken(this.element))
      const response = await fetch(this.paymentIntentUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: body
      })
      if (setupGeneration !== this._setupGeneration) return

      const data = await response.json()
      // Recheck after parsing: a newer setup may have finished while this body's JSON resolved.
      if (setupGeneration !== this._setupGeneration) return

      if (data.error || !data.client_secret) {
        // login_url: this email already has an account (Flows::ExistingDonorCheckout).
        this.clearPaymentLoading()
        this.showError(data.error || "Could not start payment. Please try again.", data.login_url)
        return
      }

      const stripeOptions = data.stripe_account_id ? { stripeAccount: data.stripe_account_id } : {}
      this.stripe = window.Stripe(this.stripeApiKeyValue, stripeOptions)
      this.elements = this.stripe.elements({ clientSecret: data.client_secret, appearance: { theme: "stripe" } })
      // Wallets (Google Pay, Apple Pay, Link) render via the dedicated Express Checkout Element
      // below instead, so the accordion doesn't also show its own auto-detected (and easy-to-miss)
      // wallet row alongside it.
      const paymentElement = this.elements.create("payment", {
        fields: { billingDetails: "auto" },
        wallets: { applePay: "never", googlePay: "never", link: "never" },
        // ACH only appears when billing country is US. The donor already chose a country on
        // personal_info; without this, Stripe infers from locale/IP (often GB for HLI testers)
        // and hides US bank account while still showing Bacs for GBP (currency-gated).
        defaultValues: { billingDetails: this.billingDetails() },
        // Explicit tabs, not accordion's default auto-collapse-to-one-method behavior -- Card and
        // Bank account/Bacs Debit should always read as a menu of options to pick between, the way
        // Bacs Debit already does for GBP donors, not auto-expand straight into card fields.
        layout: { type: "tabs" }
      })
      this.paymentElementTarget.innerHTML = ""
      paymentElement.mount(this.paymentElementTarget)

      this.intentType = data.intent_type || "payment"
      // A SetupIntent saves the method without charging -- the Express Checkout wallet row is a
      // "pay now" surface, so it's only offered when something is charged today.
      if (this.hasBacsSetupTarget) this.bacsSetupTarget.classList.toggle("is-hidden", !data.bacs_setup_available)
      if (this.hasExpressCheckoutElementTarget && this.intentType === "setup") {
        this.expressCheckoutElementTarget.classList.add("is-invisible")
        this.expressCheckoutElementTarget.innerHTML = ""
      } else if (this.hasExpressCheckoutElementTarget) {
        // googlePay/applePay: "always" -- Payment Element's own auto wallet detection only shows a
        // real wallet button when the browser already has an active saved card; otherwise it falls
        // back to a plain list row that does nothing until "Donate now" is clicked. Forcing "always"
        // here guarantees Stripe renders each wallet's own button as soon as the platform supports
        // it, each sitting at the same level as Card/US bank account, not nested behind a click.
        const expressCheckoutElement = this.elements.create("expressCheckout", {
          // Explicitly "never" for every wallet this row isn't meant to offer -- paymentMethods
          // defaults unlisted entries to "auto", so PayPal/Klarna/Amazon Pay would otherwise
          // silently appear here too if enabled on the Stripe Dashboard.
          paymentMethods: {
            googlePay: "always", applePay: "always", link: "auto",
            paypal: "never", klarna: "never", amazonPay: "never"
          },
          // "plain" for both -- logo/wordmark only, no "Donate with"/"Buy with" verb text.
          buttonType: { googlePay: "plain", applePay: "plain" }
        })
        // Hidden via visibility (is-invisible), never display: none -- setupPaymentElement()
        // recreates this element on tip/amount changes, and a display: none parent can leave a
        // mounted iframe unable to size itself, or stop it from ever reporting a later
        // availablepaymentmethodschange (e.g. the donor signs into a wallet mid-session). Reset
        // to hidden before every (re)mount so a stale "wallets available" state from a prior mount
        // can't flash the row before this mount's own eligibility check runs.
        this.expressCheckoutElementTarget.classList.add("is-invisible")
        this.expressCheckoutElementTarget.innerHTML = ""
        expressCheckoutElement.mount(this.expressCheckoutElementTarget)
        expressCheckoutElement.on("confirm", () => this.confirmAndSubmit())
        // Stripe returns paymentMethods as undefined when no wallet will render on this browser.
        expressCheckoutElement.on("availablepaymentmethodschange", ({ paymentMethods }) => {
          this.expressCheckoutElementTarget.classList.toggle("is-invisible", !paymentMethods)
        })
      }

      if (setupGeneration !== this._setupGeneration) return
      this.intentKey = intentKey
      this.checkoutAttemptIdTarget.value = data.checkout_attempt_id
    } catch (err) {
      if (setupGeneration !== this._setupGeneration) return
      // Superseded wait — a newer setup (or leaving Review) aborted this one on purpose.
      if (err?.name === "AbortError") return
      const challengeFailed = err instanceof PaymentChallengeError
      // Donor-retryable Turnstile timeouts are not engineering errors (DONATIONAL-TW).
      // Fatal configuration failures (invalid sitekey, domain not authorized) still report --
      // waitForPaymentChallenge marks those PaymentChallengeError.fatal.
      if (!challengeFailed || err.fatal) {
        console.error("Donation flow payment setup error:", err)
        reportClientError(this.clientErrorUrlValue, "payment setup", err)
      }
      this.clearPaymentLoading()
      // Only PaymentChallengeError messages are donor-safe (Turnstile). Raw fetch/JSON/Stripe text
      // stays behind the generic line.
      const detail = challengeFailed ? err.message.trim() : ""
      this.showError(detail || "Something went wrong setting up payment. Please try again.")
    }
  }

  showPaymentLoading() {
    if (!this.hasPaymentElementTarget) return
    this.paymentElementTarget.innerHTML = "<p class=\"donation-flow__helper-text\">Loading payment options…</p>"
  }

  clearPaymentLoading() {
    if (!this.hasPaymentElementTarget || this.elements) return
    this.paymentElementTarget.innerHTML = ""
  }

  // Called by donation_wizard_controller.js#submitForm instead of a plain form submit.
  async confirmAndSubmit() {
    if (this.isSubmitting) return
    if (!this.elements) {
      this.showError("Payment isn't ready yet. Please wait a moment and try again.")
      return
    }

    // A tip change may not have re-issued the PaymentIntent yet -- never confirm a stale amount.
    if (this.currentIntentKey() !== this.intentKey) {
      this.showError("Updating your total — please try again in a moment.")
      this.queueSetupPaymentElement(0)
      return
    }

    this.clearError()
    const submitResult = await this.elements.submit()
    if (submitResult?.error) {
      this.showError(submitResult.error.message || "Please complete all required fields.")
      return
    }

    this.isSubmitting = true
    this.toggleProgressButton(true)
    if (this.intentType === "setup") {
      try {
        await this.confirmSetupAndSubmit()
      } finally {
        this.isSubmitting = false
        this.toggleProgressButton(false)
      }
      return
    }
    try {
      const result = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: this.billingDetails()
          }
        },
        redirect: "if_required"
      })

      if (result.error) {
        this.showError(result.error.message || "Payment failed.")
        return
      }

      const status = result.paymentIntent?.status
      if (status === "succeeded" || status === "processing") {
        const methodType = result.paymentIntent.payment_method_types?.[0] || "card"
        this.paymentIntentIdTarget.value = result.paymentIntent.id
        this.paymentProcessorMethodIdTarget.value = result.paymentIntent.payment_method || ""
        this.paymentProcessorMethodTypeTarget.value = methodType
        if (this.hasPaymentMethodIdTarget) this.paymentMethodIdTarget.value = methodType
        this.element.submit()
      } else {
        this.showError("Payment was not completed. Please try again.")
      }
    } catch (err) {
      console.error("Donation flow payment confirm error:", err)
      reportClientError(this.clientErrorUrlValue, "payment confirm", err)
      this.showError(err.message || "Payment failed. Please try again.")
    } finally {
      this.isSubmitting = false
      this.toggleProgressButton(false)
    }
  }

  // Future-start pledge with no meantime donation: save the payment method, charge nothing.
  // requires_action is a US bank account awaiting micro-deposit verification -- accepted, since
  // nothing is charged until the pledge starts (Contributions::CreateSubscriptionFromSetupIntent).
  async confirmSetupAndSubmit() {
    try {
      const result = await this.stripe.confirmSetup({
        elements: this.elements,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: { billing_details: this.billingDetails() }
        },
        redirect: "if_required"
      })
      if (result.error) {
        this.showError(result.error.message || "Your payment details could not be saved.")
        return
      }

      const setupIntent = result.setupIntent
      if (!["succeeded", "processing", "requires_action"].includes(setupIntent?.status)) {
        this.showError("Your payment details were not saved. Please try again.")
        return
      }
      const methodType = setupIntent.payment_method_types?.[0] || "card"
      if (this.hasSetupIntentIdTarget) this.setupIntentIdTarget.value = setupIntent.id
      this.paymentProcessorMethodIdTarget.value = setupIntent.payment_method || ""
      this.paymentProcessorMethodTypeTarget.value = methodType
      if (this.hasPaymentMethodIdTarget) this.paymentMethodIdTarget.value = methodType
      this.element.submit()
    } catch (err) {
      console.error("Donation flow setup confirm error:", err)
      reportClientError(this.clientErrorUrlValue, "setup confirm", err)
      this.showError(err.message || "Your payment details could not be saved. Please try again.")
    }
  }

  // UK bank account for a future pledge with no meantime donation: off to Stripe's hosted page,
  // which returns to /make-donation/bacs-return (DonationFlowsController#bacs_setup_return).
  async startBacsSetup() {
    this.clearError()
    const checkoutAttemptId = this.checkoutAttemptIdTarget.value
    if (!checkoutAttemptId || this.intentType !== "setup") {
      this.showError("Payment isn't ready yet. Please wait a moment and try again.")
      return
    }
    try {
      const response = await fetch(this.bacsSetupUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ checkout_attempt_id: checkoutAttemptId })
      })
      const data = await response.json()
      if (data.error || !data.url) {
        this.showError(data.error || "Could not start UK bank account setup. Please try again.")
        return
      }
      window.location.assign(data.url)
    } catch (err) {
      console.error("Donation flow Bacs setup error:", err)
      reportClientError(this.clientErrorUrlValue, "Bacs setup", err)
      this.showError("Could not start UK bank account setup. Please try again.")
    }
  }

  formValue(field) {
    return this.element.querySelector(`[name="donation_form[${field}]"]`)?.value?.trim() || undefined
  }

  billingDetails() {
    return stripeBillingDetails({
      firstName: this.formValue("first_name"),
      lastName: this.formValue("last_name"),
      email: this.formValue("email"),
      address: {
        line1: this.formValue("address_line1"),
        line2: this.formValue("address_line2"),
        city: this.formValue("city"),
        state: this.formValue("state"),
        postal_code: this.formValue("postal_code"),
        country: this.formValue("country")
      },
      nameToIso: this.hasCountryIsoValue ? this.countryIsoValue : {}
    })
  }

  toggleProgressButton(disabled) {
    const button = this.element.querySelector('[data-donation-wizard-target="progress"]')
    if (button) button.disabled = disabled
  }

  showError(message, loginUrl = null) {
    if (!this.hasPaymentErrorsTarget) return
    this.paymentErrorsTarget.textContent = message
    if (loginUrl) {
      const link = document.createElement("a")
      link.href = loginUrl
      link.textContent = "Log in →"
      this.paymentErrorsTarget.append(" ", link)
    }
    this.paymentErrorsTarget.classList.remove("is-hidden")
  }

  clearError() {
    if (!this.hasPaymentErrorsTarget) return
    this.paymentErrorsTarget.textContent = ""
    this.paymentErrorsTarget.classList.add("is-hidden")
  }
}
